import type { QueryKey } from '@tanstack/react-query';

/**
 * When a notification arrives, the screens it concerns refresh at once ("đang ở màn đấy thì hiển thị luôn").
 * Maps a notification type (API: *NotificationHandler.Type*) to query-key prefixes to invalidate. TanStack refetches only ACTIVE
 * queries (what is on screen) and marks the rest stale, so this is cheap. Only server data is refetched — forms keep their input.
 * Prefixes mirror the key factories: approvalKeys (features/approvals/shared), ck (api/commercial, api/commission), dk (api/delivery),
 * fk (features/finance/common), sales/crm roots and ['dashboard'].
 */
const DASHBOARD: QueryKey = ['dashboard'];

const APPROVAL_SUBJECTS: QueryKey[] = [
  ['sales'], // quotation approved/returned
  ['commercial', 'contracts'],
  ['commercial', 'orders'],
  ['commercial', 'change-requests'],
  ['operations', 'handovers'], // an approved order creates a handover
  ['finance', 'receipt'], // verification override
  ['finance', 'refunds'],
  ['finance', 'refund'],
  ['finance', 'adjustments'],
  ['finance', 'adjustment'],
  ['commission', 'adjustments'],
  ['commission', 'policies'],
  ['commission', 'policy'],
  ['crm', 'customers'],
  ['crm', 'classification-overrides'],
];

const RECEIPTS: QueryKey[] = [
  ['finance', 'receipts'],
  ['finance', 'receipt'],
  ['finance', 'desk'],
  ['finance', 'receivables'],
  ['finance', 'receivable'],
  ['finance', 'contract'],
  ['finance', 'allocations'],
];

const MAP: Record<string, QueryKey[]> = {
  APPROVAL_ASSIGNED: [['approvals']],
  APPROVAL_OVERDUE: [['approvals']],
  APPROVAL_COMPLETED: [['approvals'], ...APPROVAL_SUBJECTS],
  HANDOVER_PENDING: [['operations', 'handovers'], ['commercial', 'orders']],
  HANDOVER_ACCEPTED: [['operations', 'handovers'], ['commercial', 'orders'], ['delivery', 'projects']],
  HANDOVER_DECLINED: [['operations', 'handovers'], ['commercial', 'orders']],
  RECEIPT_SUBMITTED: RECEIPTS,
  RECEIPT_VERIFIED: RECEIPTS,
  RECEIPT_RETURNED: RECEIPTS,
  COMMISSION_PAYOUT_PAID: [
    ['commission', 'payouts'],
    ['commission', 'payout'],
    ['commission', 'accruals'],
    ['commission', 'accrual'],
    ['commission', 'me'],
    ['commission', 'desk'],
  ],
  TASK_ASSIGNED: [
    ['delivery', 'projects'],
    ['delivery', 'tasks'],
  ],
  QUOTE_ACCEPTED: [['sales'], ['crm', 'opportunities']],
  QUOTE_REJECTED: [['sales'], ['crm', 'opportunities']],
};

/** Query-key prefixes to invalidate for these notification types (deduplicated; always includes the dashboard). */
export function keysForNotificationTypes(types: Iterable<string>): QueryKey[] {
  const seen = new Set<string>();
  const out: QueryKey[] = [];
  for (const k of [...[...types].flatMap((t) => MAP[t] ?? []), DASHBOARD]) {
    const id = JSON.stringify(k);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(k);
  }
  return out;
}
